import {
  Box,
  Checkbox,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { BrandsContext } from "contexts/Brands";
import { useContext, useState } from "react";
import { BentoCategory, SearchParams } from "schemas/dashboard";

import { trackEvent } from "utils/tracking";
import { useSearchBrand } from "utils/useSearchBrand";

interface Props {
  category: BentoCategory;
}

const ALL = "All";

const TopLevelCategory = ({ category: topLevelCategory }: Props) => {
  const {
    addCategoryQueryToSearch,
    addAllCategoryIdQueryToSearch,
    removeCategoryIdWithAllSelected,
  } = useSearchBrand();
  const { discoverFilterParams } = useContext(BrandsContext);

  const selectedCategoryIds = discoverFilterParams.selectedCategoryIds;

  const [open, setOpen] = useState(false);

  const isAllSelected = Boolean(
    selectedCategoryIds?.find((x) => x.key === `${topLevelCategory.id}:all`),
  );

  const handleClick = () => {
    trackEvent("Category Expanded", { Category: topLevelCategory.id });
    setOpen((prev) => !prev);
  };

  const handleSelectCategory = (category: BentoCategory, value: string) => {
    if (value === ALL) {
      addAllCategoryIdQueryToSearch(
        SearchParams.CATEGORY_ID_DISCOVER,
        category.id,
      );
    } else {
      if (isAllSelected) {
        removeCategoryIdWithAllSelected(
          SearchParams.CATEGORY_ID_DISCOVER,
          parseInt(value),
          category.id,
        );
      } else {
        addCategoryQueryToSearch(SearchParams.CATEGORY_ID_DISCOVER, value);
      }
    }
  };

  return (
    <Box key={`${topLevelCategory.id}`}>
      <ListItemButton
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: "10px",
          py: "8px",
          borderRadius: "10px",
        }}
        disableRipple
        onClick={handleClick}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 500,
            color: "#364153",
            lineHeight: "16px",
          }}
        >
          {topLevelCategory.name}
        </Typography>
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: "50%",
            backgroundColor: "#f3f4f6",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Box
            component="i"
            className={open ? "fa-solid fa-minus" : "fa-solid fa-plus"}
            sx={{ fontSize: 9, color: "#6a7282", lineHeight: 1 }}
          />
        </Box>
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List dense component="div" disablePadding>
          {topLevelCategory?.subcategories?.map((subcategory) => (
            <ListItemButton
              key={`${subcategory.id}`}
              sx={{ pl: 2, py: 0 }}
              onClick={() =>
                handleSelectCategory(topLevelCategory, `${subcategory.id}`)
              }
            >
              <Checkbox
                edge="start"
                checked={
                  Boolean(
                    selectedCategoryIds?.find(
                      (x) => x.key === `${subcategory.id}`,
                    ),
                  ) || isAllSelected
                }
                tabIndex={-1}
                disableRipple
                icon={<Box component="i" className="fa-regular fa-square" />}
                checkedIcon={
                  <Box component="i" className="fa-solid fa-square-check" />
                }
                disabled={isAllSelected}
              />

              <ListItemText
                primary={subcategory.displayName || subcategory.name}
              />
            </ListItemButton>
          ))}

          <ListItemButton
            disableRipple
            sx={{ pl: 2, py: 0 }}
            onClick={() => handleSelectCategory(topLevelCategory, ALL)}
          >
            <Checkbox
              edge="start"
              checked={isAllSelected}
              tabIndex={-1}
              disableRipple
              icon={<Box component="i" className="fa-regular fa-square" />}
              checkedIcon={
                <Box component="i" className="fa-solid fa-square-check" />
              }
            />
            <ListItemText primary={"All"} />
          </ListItemButton>
        </List>
      </Collapse>
    </Box>
  );
};

export default TopLevelCategory;
